import React, { useEffect, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import { useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { pharmacyRequestPasswordReset } from '../../../redux/middleware/user/pharmacy/resetPassword';
import { showToast, ReactToastifyAdapter } from '../../../scenes/user/pharmacy/config/helpers';
import { toast } from 'react-toastify';

const PharmacyForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: ''
  });
  const [errors, setErrors] = useState({});
  const { isLoading, successMessage, error } = useSelector(
    (state) => state.hospitalRequestPasswordReset || {}
  );

  useEffect(() => {
    if (error) showToast(ReactToastifyAdapter, 'error', error);
    if (successMessage) {
      showToast(ReactToastifyAdapter, 'success', successMessage);
      navigate('/app/pharmacy/login');
    }
  }, [error, successMessage]);

  const resetForm = () => {
    setFormData({
      email: ''
    });
    setErrors({});
  };

  // Handle input changes and validate field in real-time
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));
    try {
      await validationSchema.validateAt(name, { [name]: value });
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error.message }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formData);
      const response = await dispatch(pharmacyRequestPasswordReset(formData.email));
      if (response.status === 200) {
        resetForm();
        showToast(
          ReactToastifyAdapter,
          'success',
          response?.data?.message || 'Password reset link sent to your email.'
        );
      }
    } catch (error) {
      if (error.name === 'ValidationError') {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        const errorMessage =
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    'Failed to send password reset email.';
        showToast(ReactToastifyAdapter, 'error', errorMessage);
      }
    }
  };
  return (
    <form onSubmit={handleSubmit} className={css(styles.form)}>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <input
          type='email'
          name='email'
          placeholder='Email'
          value={formData.email}
          onChange={handleChange}
          className={css(styles.input)}
        />
        {errors.email && <p className={css(styles.error)}>{errors.email}</p>}
      </motion.div>
      <motion.button
        type='submit'
        className={css(styles.button)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        {isLoading
          ? (
            <CircularProgress size={24} color='inherit' />
            )
          : (
              'Confirm Email'
            )}
      </motion.button>
      <motion.button
        className={css(styles.backButton)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        onClick={() => navigate('/app/pharmacy/login')}
      >
        Back to Login
      </motion.button>
    </form>
  );
};

const styles = StyleSheet.create({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem' // Ensure modern browser support
  },
  inputGroup: {
    position: 'relative'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'border-color 0.3s',
    ':focus': {
      outline: 'none',
      borderColor: '#2783F7',
      boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)'
    }
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#1c5fa8'
    }
  },
  backButton: {
    backgroundColor: 'white',
    color: '#4a5568',
    border: '1px solid #e2e8f0',
    padding: '0.75rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s, border-color 0.3s',
    ':hover': {
      backgroundColor: '#f7fafc',
      borderColor: '#cbd5e0'
    }
  },
  error: {
    color: '#e53e3e',
    fontSize: '0.875rem',
    marginTop: '0.25rem',
    position: 'absolute',
    bottom: '-1.25rem',
    left: '0'
  }
});

export default PharmacyForgotPassword;
